import { X } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { PassportStamp } from "./PassportStamp";
import { levelName, stampDate } from "./stampStyle";
import type { StampEarned } from "../../services/api/travelHealth";
import { useI18nStore } from "../../stores/i18nStore";

interface Props {
  earned: StampEarned;
  onClose: () => void;
}

/**
 * The moment a trip reaches 100% (UX_CONCEPT.md §7): the stamp lands, and a
 * new level is named. Short, silent, and it never blocks the page — the
 * checklist stays usable underneath, and the card can be closed.
 */
export const StampCelebration: React.FC<Props> = ({ earned, onClose }) => {
  const { t, language } = useI18nStore();
  const { stamp, levelUp } = earned;
  const ref = useRef<HTMLElement>(null);

  // The last tick is often far down the list; bring the stamp into view.
  useEffect(() => {
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    ref.current?.scrollIntoView?.({
      block: "nearest",
      behavior: reduced ? "auto" : "smooth",
    });
  }, [stamp.travelId]);

  return (
    <section
      ref={ref}
      role="status"
      aria-label={t.stampEarned}
      className="flex scroll-mt-24 items-center gap-4 rounded-[22px] bg-white p-5 ring-2 ring-health-700"
    >
      <PassportStamp
        city={stamp.city}
        ordinal={earned.count}
        detail={stampDate(stamp.departureDate ?? stamp.earnedAt, language)}
        size={78}
        animate
      />
      <div className="min-w-0 flex-1">
        <h2 className="font-display text-[19px] font-bold text-ink">
          {t.stampEarned}
        </h2>
        <p className="mt-1 text-sm leading-snug text-slate-600">
          {levelUp
            ? t.stampEarnedLevelUp.replace("{level}", levelName(levelUp, t))
            : t.stampEarnedBody}
        </p>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label={t.homeDismiss}
        className="-m-2 flex h-10 w-10 flex-shrink-0 items-center justify-center self-start rounded-xl text-muted-foreground hover:bg-muted"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </section>
  );
};
